import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useContext, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { useParams } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useMutateRenameTellet } from '@/reactQuery/post';
import { Input } from '../ui/input';
import { LangContext } from '@/context/LangProvider';
import { Button } from '../ui/button';
import { ECompanyThemeName } from '@/enums';
import { RenameProjectModalProps, RenameProjectProps } from '@/types';
import { showToastErrorMessage } from '@/utils/showToastErrorMessage';
import ErrorMessage from '../error-message/ErrorMessage';
import ModalToastContainer, { MODAL_TOAST_CONTAINER_ID } from '../ui/ModalToastContainer';

const validationSchema = Yup.object().shape({
  projectName: Yup.string().required('msg.newTelletNameIsRequired'),
});

function RenameTelletModal({ projectId, projectTitle }: RenameProjectModalProps) {
  const { lang } = useContext(LangContext);
  const { themeName } = useTheme();

  const defaultValues = {
    projectName: projectTitle,
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues, resolver: yupResolver(validationSchema) });
  const [openModal, setOpenModal] = useState(false);
  const { organization_id: organizationId, workspace_id: workspaceId } = useParams();
  const modalRef = useRef<HTMLDivElement>(null);
  const { mutateAsync } = useMutateRenameTellet(organizationId, workspaceId, projectId);

  const onSubmit: SubmitHandler<RenameProjectProps> = async ({ projectName }) => {
    try {
      await mutateAsync({ projectName: projectName.trim() });
      setOpenModal(false);
      toast.success(lang.get('msg.projectSuccessfullyRenamed'));
      reset();
    } catch (error) {
      showToastErrorMessage(error, { containerId: MODAL_TOAST_CONTAINER_ID });
    }
  };

  const renameTelletLabel =
    themeName === ECompanyThemeName.EMPATHY
      ? lang.get('msg.renameYourTellet_empathy')
      : lang.get('msg.renameYourTellet');
  const projectNameError = errors?.projectName?.message;

  return (
    <AlertDialog open={openModal}>
      <AlertDialogTrigger onClick={() => setOpenModal(true)} asChild>
        <p className="py-1 pl-2 text-sm cursor-pointer hover:bg-secondary-background">
          {lang.get('msg.rename')}
        </p>
      </AlertDialogTrigger>
      <AlertDialogContent ref={modalRef} className="gap-0">
        <form onSubmit={handleSubmit(onSubmit)}>
          <AlertDialogHeader>
            <AlertDialogTitle>{renameTelletLabel}</AlertDialogTitle>
            <div className="flex flex-col pt-5 pb-12">
              <label className="relative flex flex-col w-full gap-1">
                <Input
                  hasError={!!projectNameError}
                  {...register('projectName')}
                  placeholder={renameTelletLabel}
                  className="w-full"
                />
                {projectNameError && <ErrorMessage message={projectNameError} />}
              </label>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setOpenModal(false)}>
              {lang.get('msg.cancel')}
            </AlertDialogCancel>
            <Button variant="default" type="submit" disabled={isSubmitting}>
              {lang.get('msg.submit')}
            </Button>
          </AlertDialogFooter>
        </form>
        <ModalToastContainer targetRef={modalRef} />
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default RenameTelletModal;
